import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { toast } from "react-toastify";
// import {
//   MODULE_CONFIG,
//   getPortalUrl,
//   formatAddress,
//   formatDate,
//   formatTime,
//   capitalize,
// } from "./shared";
import {
  MODULE_CONFIG,
  getPortalUrl,
  getTenantLogoUrl,
  formatAddress,
  formatDate,
  formatTime,
  capitalize,
} from "./shared";
import {
  FiArrowLeft,
  FiEdit3,
  FiExternalLink,
  FiCopy,
  FiMail,
  FiPhone,
  FiMapPin,
  FiGlobe,
  FiUsers,
  FiCheckCircle,
  FiAlertOctagon,
  FiLayers,
  FiLink,
  FiUploadCloud,
  FiTrash2,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch tenant details
  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    loader(true);

    try {
      const endpoint = MODULE_CONFIG.endpoints.detail(id);
      const res = await ApiClient.get(endpoint);

      if (res?.success && (res?.data || res?.data?.tenant)) {
        const data = res?.data?.tenant || res?.data?.data || res?.data;
        setTenant(data);
      } else {
        toast.error("Unable to find tenant record.");
      }
    } catch (err) {
      console.error("Failed to fetch tenant detail:", err);
      toast.error("Failed to load tenant details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Copy helper
  const handleCopy = (text, label = "Text") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard!`);
  };

  // Change lifecycle status
  const handleStatusChange = (newStatus) => {
    const companyName = tenant?.company_name || tenant?.name || "Tenant";

    swal({
      title: `Update Status to ${capitalize(newStatus)}?`,
      text: `Are you sure you want to change the status of "${companyName}" to ${newStatus}?${
        newStatus === "SUSPENDED"
          ? " Suspended tenants are blocked by the tenant resolver."
          : ""
      }`,
      icon: newStatus === "SUSPENDED" ? "warning" : "info",
      buttons: ["Cancel", "Confirm Update"],
      dangerMode: newStatus === "SUSPENDED" || newStatus === "CANCELLED",
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const endpoint = MODULE_CONFIG.endpoints.statusChange(id);
        const res = await ApiClient.put(endpoint, { status: newStatus });
        if (res?.success) {
          toast.success(`Tenant status changed to ${newStatus}.`);
          fetchDetail();
        } else {
          toast.error(res?.message || "Failed to update status.");
        }
      } catch (err) {
        console.error("Failed to update tenant status:", err);
        toast.error("Failed to update status.");
      } finally {
        loader(false);
      }
    });
  };

  // Upload logo directly
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file (PNG, JPG, SVG).");
      return;
    }

    setUploadingLogo(true);
    loader(true);

    try {
      // First try direct tenant logo update
      const tenantLogoEndpoint = MODULE_CONFIG.endpoints.tenantLogo(id);
      let res = await ApiClient.postFormData(tenantLogoEndpoint, {
        logo: file,
        file: file,
      });

      // If direct route failed, try upload/logo then update tenant
      if (!res?.success) {
        const uploadRes = await ApiClient.postFormData(
          MODULE_CONFIG.endpoints.uploadLogo,
          { file: file }
        );
        if (uploadRes?.success) {
          const logoUrl = uploadRes?.fileUrl || uploadRes?.filePath || uploadRes?.data?.url;
          res = await ApiClient.put(MODULE_CONFIG.endpoints.update(id), {
            logo: logoUrl,
          });
        }
      }

      if (res?.success) {
        toast.success("Logo uploaded successfully.");
        fetchDetail();
      } else {
        toast.error(res?.message || "Failed to upload logo.");
      }
    } catch (err) {
      console.error("Failed to upload logo:", err);
      toast.error("Failed to upload logo.");
    } finally {
      setUploadingLogo(false);
      loader(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Delete tenant
  const handleDelete = () => {
    const companyName = tenant?.company_name || tenant?.name || "Tenant";

    swal({
      title: "Delete Tenant Organization?",
      text: `Are you sure you want to delete "${companyName}"? This will deactivate all assigned users and disconnect QuickBooks.`,
      icon: "warning",
      buttons: ["Cancel", "Delete Tenant"],
      dangerMode: true,
      className: "smallmodal",
    }).then(async (confirmed) => {
      if (!confirmed) return;
      loader(true);
      try {
        const endpoint = MODULE_CONFIG.endpoints.delete(id);
        const res = await ApiClient.delete(endpoint);
        if (res?.success) {
          toast.success("Tenant organization deleted successfully.");
          navigate(MODULE_CONFIG.routeBase);
        } else {
          toast.error(res?.message || "Failed to delete tenant.");
        }
      } catch (err) {
        console.error("Delete error:", err);
        toast.error("Failed to delete tenant.");
      } finally {
        loader(false);
      }
    });
  };

  if (loading && !tenant) {
    return (
      <Layout>
        <div className="py-24 text-center">
          <div className="w-10 h-10 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm font-black text-slate-900">Loading Tenant Dossier...</p>
        </div>
      </Layout>
    );
  }

  if (!tenant) {
    return (
      <Layout>
        <div className="bg-white rounded-2xl p-8 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] text-center space-y-4 max-w-lg mx-auto my-12">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-300 text-slate-700 flex items-center justify-center mx-auto">
            <HiOutlineBuildingOffice2 className="w-6 h-6" />
          </div>
          <h2 className="text-base font-black text-slate-900">Tenant Not Found</h2>
          <p className="text-xs font-semibold text-slate-700">
            The requested tenant organization could not be loaded or may have been deleted.
          </p>
          <button
            type="button"
            onClick={() => navigate(MODULE_CONFIG.routeBase)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#115B9D] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span>Return to Tenants</span>
          </button>
        </div>
      </Layout>
    );
  }

  // Extract fields
  const companyName = tenant?.company_name || tenant?.name || "Untitled Organization";
  const subdomain = tenant?.subdomain || "";
  const portalUrl = getPortalUrl(subdomain);
  const portalTitle = tenant?.portal_title || "Commercial Portal";

  const primaryColor = tenant?.primary_color || "#002B49";
  const secondaryColor = tenant?.secondary_color || "#005A9C";
  const accentColor = tenant?.accent_color || "#EAA812";

  const statusKey = (tenant?.status || "ACTIVE").toUpperCase();
  const statusConfig = MODULE_CONFIG.statusMap[statusKey] || {
    label: capitalize(statusKey),
    bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
    dotClass: "bg-slate-600",
  };

  // const logo = tenant?.logo || tenant?.logoUrl || tenant?.fileUrl;
  const rawLogo = tenant?.logo_url || tenant?.logo || tenant?.logoUrl || tenant?.fileUrl;
  const logo = getTenantLogoUrl(rawLogo);

  const supportEmail = tenant?.support_email || "";
  const contactEmail = tenant?.contact_email || "";
  const supportPhone = tenant?.support_phone || "";
  const contactPhone = tenant?.contact_phone || "";

  const address = tenant?.address;
  const addressText = formatAddress(address);

  const subscription = tenant?.subscription || tenant?.plan || null;
  const quickbooks = tenant?.quickbooks || tenant?.quickBooks || null;

  const adminUsers = Array.isArray(tenant?.adminUsers)
    ? tenant.adminUsers
    : Array.isArray(tenant?.users)
    ? tenant.users
    : tenant?.adminEmail
    ? [
        {
          name: `${tenant?.adminFirstName || ""} ${tenant?.adminLastName || ""}`.trim() || "Primary Admin",
          email: tenant?.adminEmail,
          phone: tenant?.adminPhone || contactPhone,
          role: "Tenant Admin",
          createdAt: tenant?.createdAt,
        },
      ]
    : [];

  const createdAt = tenant?.createdAt || tenant?.created_at;
  const updatedAt = tenant?.updatedAt || tenant?.updated_at;

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden space-y-5 sm:space-y-6 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & BREADCRUMB ACTIONS                       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Tenants List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
                  {companyName}
                </h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-wide border whitespace-nowrap shadow-2xs ${statusConfig.bgClass}`}
                >
                  <span className={`w-2 h-2 rounded-full ${statusConfig.dotClass}`} />
                  {statusConfig.label}
                </span>
              </div>
              <p className="text-xs font-bold text-slate-700">
                Tenant Organization Dossier, Multi-Tenant Domain & Integration Settings
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            {subdomain && (
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-xs"
                title="Open Customer Portal"
              >
                <FiExternalLink className="w-3.5 h-3.5 text-[#115B9D]" />
                <span>Visit Portal</span>
              </a>
            )}

            <button
              type="button"
              onClick={() => navigate(`${MODULE_CONFIG.routeBase}/edit/${id}`)}
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
            >
              <FiEdit3 className="w-3.5 h-3.5" />
              <span>Edit Tenant</span>
            </button>

            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3.5 h-10 rounded-xl border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 text-xs font-bold transition-all cursor-pointer shadow-xs"
              title="Delete Tenant"
            >
              <FiTrash2 className="w-3.5 h-3.5 text-rose-700" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. BALANCED DOSSIER LAYOUT (ROW 1: HALF-HALF, ROW 2: FULL) */}
        {/* ======================================================== */}
        <div className="space-y-6">
          {/* ROW 1: 50/50 Balanced Half-Half Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {/* Card 1: Company Profile & Multi-Tenant Details */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-5">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                    <HiOutlineBuildingOffice2 className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Company & Multi-Tenant Profile
                  </h3>
                </div>

                <div className="space-y-4 pt-4 text-xs">
                  {/* Multi-Tenant Subdomain Tile */}
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
                    <span className="text-[10px] font-bold text-slate-600 uppercase block">
                      Assigned Customer Portal Subdomain
                    </span>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-black text-sm text-[#115B9D] bg-white px-2.5 py-1 rounded-lg border border-slate-300 shadow-2xs">
                          {subdomain || "no-subdomain"}
                        </span>
                        <span className="text-xs font-bold text-slate-700">
                          .{MODULE_CONFIG.portalBaseDomain}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => handleCopy(portalUrl, "Portal URL")}
                          className="p-1.5 rounded-lg bg-white border border-slate-300 text-slate-700 hover:text-slate-950 transition-colors cursor-pointer"
                          title="Copy Full URL"
                        >
                          <FiCopy className="w-3.5 h-3.5" />
                        </button>
                        <a
                          href={portalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg bg-white border border-slate-300 text-[#115B9D] hover:text-[#0e4b82] transition-colors cursor-pointer"
                          title="Open in New Tab"
                        >
                          <FiExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Key-Value Details */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between py-1 border-b border-slate-100">
                      <span className="font-bold text-slate-600">Portal Title:</span>
                      <span className="font-black text-slate-950 text-right truncate max-w-[220px]">
                        {portalTitle}
                      </span>
                    </div>

                    {contactEmail && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Contact Email:</span>
                        <a
                          href={`mailto:${contactEmail}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1 truncate max-w-[220px]"
                        >
                          <FiMail className="w-3 h-3 shrink-0" />
                          <span className="truncate">{contactEmail}</span>
                        </a>
                      </div>
                    )}

                    {supportEmail && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Support Email:</span>
                        <a
                          href={`mailto:${supportEmail}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1 truncate max-w-[220px]"
                        >
                          <FiMail className="w-3 h-3 shrink-0" />
                          <span className="truncate">{supportEmail}</span>
                        </a>
                      </div>
                    )}

                    {contactPhone && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Contact Phone:</span>
                        <a
                          href={`tel:${contactPhone}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1"
                        >
                          <FiPhone className="w-3 h-3 shrink-0" />
                          <span>{contactPhone}</span>
                        </a>
                      </div>
                    )}

                    {supportPhone && (
                      <div className="flex items-center justify-between py-1 border-b border-slate-100">
                        <span className="font-bold text-slate-600">Support Phone:</span>
                        <a
                          href={`tel:${supportPhone}`}
                          className="font-bold text-[#115B9D] hover:underline flex items-center gap-1"
                        >
                          <FiPhone className="w-3 h-3 shrink-0" />
                          <span>{supportPhone}</span>
                        </a>
                      </div>
                    )}

                    <div className="flex items-start justify-between py-1 border-b border-slate-100 gap-3">
                      <span className="font-bold text-slate-600 shrink-0">Address:</span>
                      <span className="font-medium text-slate-800 text-right">
                        {addressText}
                      </span>
                    </div>

                    <div className="flex items-center justify-between py-1">
                      <span className="font-bold text-slate-600">Created:</span>
                      <span className="font-bold text-slate-900">
                        {createdAt ? `${formatDate(createdAt)} at ${formatTime(createdAt)}` : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Branding, Subscription & QuickBooks */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-5">
              <div>
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                    <FiLayers className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Brand Palette & Platform Connectors
                  </h3>
                </div>

                <div className="space-y-4 pt-4 text-xs">
                  {/* Logo Display & Direct Upload */}
                  <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {logo ? (
                        <img
                          src={logo}
                          alt="Tenant Logo"
                          className="w-12 h-12 rounded-xl object-contain bg-white border border-slate-300 p-1 shrink-0 shadow-2xs"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-white text-base shrink-0 shadow-xs"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {companyName.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <span className="text-[10px] font-bold text-slate-600 uppercase block">
                          Official Brand Logo
                        </span>
                        <span className="text-xs font-bold text-slate-900">
                          {logo ? "Logo Image Uploaded" : "Default Monogram Logo"}
                        </span>
                      </div>
                    </div>

                    {/* Logo upload button */}
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
                        title="Upload/Replace Logo"
                      >
                        <FiUploadCloud className="w-3.5 h-3.5 text-[#115B9D]" />
                        <span>{uploadingLogo ? "Uploading..." : "Change Logo"}</span>
                      </button>
                    </div>
                  </div>


                  {/* QuickBooks & Subscription Connectors */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-[10px] font-bold text-slate-600 uppercase block">
                        Subscription Tier
                      </span>
                      <div className="font-black text-xs text-slate-900">
                        {subscription?.name || subscription?.planName || "Enterprise Tier"}
                      </div>
                      <span className="text-[11px] font-medium text-slate-600">
                        {subscription?.status ? `Status: ${capitalize(subscription.status)}` : "Platform Plan"}
                      </span>
                    </div>

                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-[10px] font-bold text-slate-600 uppercase block">
                        QuickBooks Connector
                      </span>
                      <div className="flex items-center gap-1.5 font-black text-xs text-slate-900">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            quickbooks?.isConnected ? "bg-emerald-500" : "bg-slate-400"
                          }`}
                        />
                        <span>{quickbooks?.isConnected ? "Connected" : "Not Linked"}</span>
                      </div>
                      <span className="text-[11px] font-medium text-slate-600 truncate block">
                        {quickbooks?.realmId ? `Realm: ${quickbooks.realmId}` : "Sync Pending"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2: Full Width Tenant Admin Users Table */}
          <div className="w-full bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3 flex-wrap">
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200 flex items-center justify-center font-bold">
                  <FiUsers className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Tenant Admin Users & Authorized Accounts ({adminUsers.length})
                </h3>
              </div>
            </div>

            {adminUsers.length === 0 ? (
              <div className="py-8 text-center text-slate-600 font-medium text-xs">
                No assigned admin users found for this tenant organization.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-black uppercase text-slate-700 tracking-wider">
                      <th className="py-3 px-4">Admin Name</th>
                      <th className="py-3 px-4">Email Address</th>
                      <th className="py-3 px-4">Contact Phone</th>
                      <th className="py-3 px-4 text-center">Assigned Role</th>
                      <th className="py-3 px-4 text-right">Added Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {adminUsers.map((admin, idx) => {
                      const adminDisplayName =
                        (typeof admin?.name === "string" && admin.name) ||
                        (typeof admin?.fullName === "string" && admin.fullName) ||
                        `${admin?.firstName || ""} ${admin?.lastName || ""}`.trim() ||
                        "Tenant Admin";

                      const adminEmail = typeof admin?.email === "string" ? admin.email : "";
                      const adminPhone =
                        typeof admin?.phone === "string"
                          ? admin.phone
                          : typeof admin?.mobileNo === "string"
                          ? admin.mobileNo
                          : "";

                      const roleName =
                        typeof admin?.role === "object"
                          ? admin?.role?.name || admin?.role?.title || "Tenant Admin"
                          : typeof admin?.role === "string"
                          ? admin.role
                          : "Tenant Admin";

                      return (
                        <tr key={admin?.id || admin?._id || idx} className="hover:bg-slate-50/70">
                          <td className="py-3 px-4 font-bold text-slate-950">
                            {adminDisplayName}
                          </td>
                          <td className="py-3 px-4 font-medium text-slate-800">
                            {adminEmail ? (
                              <a
                                href={`mailto:${adminEmail}`}
                                className="text-[#115B9D] hover:underline flex items-center gap-1"
                              >
                                <FiMail className="w-3 h-3" />
                                <span>{adminEmail}</span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-3 px-4 font-medium text-slate-800">
                            {adminPhone ? (
                              <a
                                href={`tel:${adminPhone}`}
                                className="hover:underline flex items-center gap-1"
                              >
                                <FiPhone className="w-3 h-3 text-slate-500" />
                                <span>{adminPhone}</span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-slate-100 text-slate-800 border border-slate-300">
                              {roleName}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right font-medium text-slate-700">
                            {admin?.createdAt ? formatDate(admin.createdAt) : formatDate(createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
